import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';
import { DEVICE_SIZES, useWindowSize } from 'lib/hooks/useWindowSize';

import Icon from 'components/shared/Icon/Icon';

import { STEP_STATE } from '../../lib/constants';

import PhaseHeader from './PhaseHeader';
import SidebarStepItem from './SidebarStepItem';
import useSidebarRenderList from './useSidebarRenderList';

const StepSidebar = ({
  stepsState = [],
  phases = null,
  sidebarType = 'default',
  hiddenSubstepCodes = new Set(),
  restrictedStepsCodes = new Set(),
  onExpandStep,
  onCollapseStep,
  onActivateStep,
  onNavigateSubstep
}) => {
  const { device } = useWindowSize();
  const { renderList, flatSteps } = useSidebarRenderList({ stepsState, phases });

  const activeStep = useMemo(
    () => flatSteps.find((step) => step.state_status === STEP_STATE.EXPANDED),
    [flatSteps]
  );

  const [isScrolled, setIsScrolled] = useState(false);
  const [animatedStepCode, setAnimatedStepCode] = useState(null);
  const scrollRef = useRef(null);
  const previousActiveStepCodeRef = useRef(null);
  const stepRefs = useRef({});

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      setIsScrolled(scrollRef.current.scrollTop > 0);
    }
  }, []);

  useEffect(() => {
    const activeStepCode = activeStep?.code;
    if (!activeStepCode) return;

    const previousActiveStepCode = previousActiveStepCodeRef.current;
    previousActiveStepCodeRef.current = activeStepCode;

    const activeStepElement = stepRefs.current[activeStepCode];
    const scrollContainer = scrollRef.current;
    if (activeStepElement && scrollContainer) {
      const elementTop = activeStepElement.offsetTop;
      const elementHeight = activeStepElement.offsetHeight;
      const containerHeight = scrollContainer.clientHeight;
      const maxScrollTop = scrollContainer.scrollHeight - containerHeight;
      const targetScrollTop = Math.min(
        Math.max(elementTop - (containerHeight / 2 - elementHeight / 2), 0),
        maxScrollTop
      );

      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }

    if (!previousActiveStepCode || previousActiveStepCode === activeStepCode) return;

    setAnimatedStepCode(activeStepCode);
    const timeoutId = setTimeout(() => {
      setAnimatedStepCode((currentCode) => (currentCode === activeStepCode ? null : currentCode));
    }, 700);

    return () => clearTimeout(timeoutId);
  }, [activeStep?.code]);

  return (
    <div
      className={cn(
        'onboarding-header flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-white',
        device === DEVICE_SIZES.MOBILE && 'relative mb-20 h-[calc(100vh-80px)] rounded-none'
      )}>
      <div
        className={cn(
          'relative z-10 p-4 transition-shadow duration-200',
          isScrolled && 'shadow-[0_2px_8px_0_rgba(0,0,0,0.08)]'
        )}>
        <Icon icon="logo" />
      </div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="relative flex-1 overflow-y-auto overflow-x-hidden">
        <ul className="space-y-2 px-4 pb-4">
          {renderList.map((item, index) => {
            if (item.type === 'phase_header') {
              return <PhaseHeader key={`phase-${item.data.code}`} phase={item.data} />;
            }

            const step = item.data;
            const stepIndex = flatSteps.indexOf(step);
            const nextItem = renderList[index + 1];
            const prevItem = renderList[index - 1];

            return (
              <SidebarStepItem
                key={step.step_id ?? step.id ?? step.code ?? index}
                step={step}
                stepIndex={stepIndex}
                flatStepsLength={flatSteps.length}
                nextItemType={nextItem?.type}
                prevItemType={prevItem?.type}
                sidebarType={sidebarType}
                hiddenSubstepCodes={hiddenSubstepCodes}
                restrictedStepsCodes={restrictedStepsCodes}
                animatedStepCode={animatedStepCode}
                setRef={(element) => {
                  stepRefs.current[step.code] = element;
                }}
                onExpandStep={onExpandStep}
                onCollapseStep={onCollapseStep}
                onActivateStep={onActivateStep}
                onNavigateSubstep={onNavigateSubstep}
              />
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default StepSidebar;
